import { GetServerSidePropsContext } from 'next'

import { Product } from '@mc/api'
import { BogoBanner } from '@mc/bogo'
import { PURCHASE_CTA_COPY_EXP_NAME } from '@mc/growth'
import { paths } from '@mc/paths'
import {
  GLOBAL_CTAS_LPS_FLAG_NAME,
  GLOBAL_CTAS_PROMO_MODAL_FLAG_NAME,
  GLOBAL_CTAS_STICKY_FOOTER_FLAG_NAME,
  PAGE_SOURCES,
  PromoExplainerModal,
} from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { reportError } from '@mc/next-newrelic'

import { OUTCOME_PAGES_EXP_KEY } from 'experiments/pen-3596_v1'
import {
  getDefaultProduct,
  getPrimarySubcategoryForOutcome,
  getOutcomeContent,
  outcomeExists,
} from 'libs/server/api'
import { getBucket } from '@mc/next-growthbook'
import { getPageData } from 'libs/server/page-data'
import { trackServerSideEV } from 'libs/utils/trackServerSideEV'
import {
  getGrowthClassesBySubcategoryIds,
  getGrowthPrimaryCategoryBySubcategorySlug,
  getGrowthSubcategoryBySlug,
  getV2Categories,
} from 'libs/server/api/growth'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import { redirect } from 'libs/utils/redirect'
import { CourseMeta, Outcome, OutcomeContent } from 'types/data'

import { Meta } from './components/Meta'
import { OutcomePage } from './components/OutcomePage'
import { VideoContextProvider } from './components/VideoContext'
import { getRankedContent } from '../../libs/utils'

type Ret<T extends (...args: never[]) => unknown> = Awaited<ReturnType<T>>

interface OutcomesProps {
  outcomeSlug: string
  subcatSlug: string
  courseSlugToMeta: Record<string, CourseMeta>
  primaryCategory: Ret<typeof getGrowthPrimaryCategoryBySubcategorySlug>
  primarySubcategory: Ret<typeof getGrowthSubcategoryBySlug>
  otherClassesInSameSubcategories: Ret<typeof getGrowthClassesBySubcategoryIds>
  outcome: Outcome
  outcomeRankedContent: OutcomeContent[]
  annualPass: Product
}

const Outcomes = ({
  outcomeSlug,
  subcatSlug,
  courseSlugToMeta,
  primaryCategory,
  primarySubcategory,
  otherClassesInSameSubcategories,
  outcome,
  outcomeRankedContent,
  annualPass,
}: OutcomesProps) => (
  <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'Outcome' }}>
    <StaticDataProvider
      value={{
        outcomeSlug,
        subcatSlug,
        annualPass,
        courseSlugToMeta,
        primaryCategory,
        primarySubcategory,
        outcome,
        outcomeRankedContent,
        otherClassesInSameSubcategories,
      }}
      providerKey={`outcome-${outcomeSlug}`}
    >
      <VideoContextProvider>
        <PromoExplainerModal pageSource={PAGE_SOURCES.OUTCOME} />
        <BogoBanner />
        <Meta />
        <OutcomePage />
      </VideoContextProvider>
    </StaticDataProvider>
  </TrackingParamsProvider>
)

Outcomes.pageSettings = {
  rumLabel: 'Outcome',
  analyticsLabel: 'Outcome',
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  // Initial validation
  const outcomeSlug = ctx.query.slug as string
  const [isOutcomeValid, experimentBucket] = await Promise.all([
    outcomeExists(outcomeSlug),
    getBucket(ctx, OUTCOME_PAGES_EXP_KEY),
  ])

  if (!isOutcomeValid || !experimentBucket?.startsWith('variant')) {
    return redirect(ctx, paths.categories, { queryExclude: ['slug'] })
  }

  // Get extra data
  const subcatSlug = await getPrimarySubcategoryForOutcome(outcomeSlug)

  const { outcome, courseSlugToMeta } = await getOutcomeContent(
    ctx,
    outcomeSlug,
  )

  const primaryCategory = await getGrowthPrimaryCategoryBySubcategorySlug(
    ctx,
    subcatSlug,
  )
  const primarySubcategory = await getGrowthSubcategoryBySlug(ctx, subcatSlug)

  if (
    !primaryCategory ||
    !primarySubcategory ||
    Object.keys(primaryCategory).length === 0
  ) {
    return redirect(ctx, paths.categories, { queryExclude: ['slug'] })
  }

  const includeLessons = experimentBucket !== 'variant_2'
  const outcomeRankedContent = getRankedContent(
    outcome.contents,
    includeLessons,
  )

  trackServerSideEV(ctx, GLOBAL_CTAS_STICKY_FOOTER_FLAG_NAME)
  trackServerSideEV(ctx, GLOBAL_CTAS_PROMO_MODAL_FLAG_NAME)
  trackServerSideEV(ctx, GLOBAL_CTAS_LPS_FLAG_NAME)
  trackServerSideEV(ctx, PURCHASE_CTA_COPY_EXP_NAME)

  try {
    return getPageData({
      nonCriticalPromises: {
        annualPass: getDefaultProduct(ctx),
        categories: getV2Categories(ctx),
        otherClassesInSameSubcategories: getGrowthClassesBySubcategoryIds(ctx, [
          primarySubcategory.id,
        ]).catch(() => []),
      },
      propsMapper: (restProps) => ({
        outcomeSlug,
        subcatSlug,
        primaryCategory,
        primarySubcategory,
        courseSlugToMeta,
        outcome,
        outcomeRankedContent,
        ...restProps,
      }),
    })
  } catch (e) {
    reportError(e as Error, { slug: outcomeSlug })
    return pageErrorHandler(e)
  }
}

export default Outcomes
